<template>
  <div class="flex flex-col flex-wrap sm:flex-row sm:items-center">
    <div
      class="inline-flex flex-wrap"
      :class="{
        'border-r border-gray-500 max-sm:border-none md:border-r-2':
          categoryAncestors.length,
      }"
    >
      <SFLocalizedLink
        v-for="({ to, value }, index) in categoryAncestors"
        :key="`breadcrumb-${value}`"
        :data-testid="`category-breadcrumb-${index}`"
        :to="to?.['@id']"
        raw
        class="flex items-center px-2 text-lg font-medium text-gray-500 sm:text-2xl"
        :class="{
          'border-r border-gray-500 md:border-r-2': showDividerTag(
            index,
            categoryAncestors.length,
          ),
          'pl-0': index === 0,
        }"
      >
        <span class="leading-none">{{ value }}</span>
      </SFLocalizedLink>
    </div>
    <SFHeadline
      tag="h1"
      data-testid="active-category-breadcrumb"
      class="mt-1.5 !font-semibold text-gray-900 max-sm:text-2xl max-sm:leading-6 sm:mt-0"
      :class="{ 'pl-2 max-sm:pl-0': categoryAncestors.length }"
    >
      {{ computedCategoryName }}
    </SFHeadline>
  </div>
</template>

<script setup lang="ts">
import type {
  AttributesFilterItemWithValues,
  AttributesFilterValue,
  Category,
  FilterItemWithValues,
} from '@scayle/storefront-nuxt'
import { computed } from 'vue'
import {
  useAppliedFilters,
  type AppliedAttributeValues,
} from '@scayle/storefront-product-listing/composables'
import { capitalCase } from 'change-case'
import { useCurrentShop } from '@scayle/storefront-nuxt/composables'
import SFLocalizedLink from '../SFLocalizedLink.vue'
import type { BreadcrumbItem } from '~/types/breadcrumbs'
import { useBreadcrumbs, useFilter } from '~/composables'
import { SFHeadline } from '#storefront-ui/components'
import { showDividerTag } from '#storefront-ui'
import { useRoute } from '#app'
import type { FilterValue } from '~/composables/useSizeSorting'
import { GENDER_MAPPING, GENDER_MAPPING_POLISH } from '~/constants'

const { category } = defineProps<{
  category: Category
  productsCount?: number
  productsFetching?: boolean
}>()

const { getBreadcrumbsFromCategory } = useBreadcrumbs()

const categoryAncestors = computed<BreadcrumbItem[]>(() =>
  getBreadcrumbsFromCategory(category),
)
const route = useRoute()
const { availableFilters } = useFilter(() => category.id)
const { appliedAttributeValues } = useAppliedFilters(route)

const isPolishShop = computed(() => useCurrentShop().value?.locale === 'pl-PL')

const genderMapping = computed(() =>
  isPolishShop.value ? GENDER_MAPPING_POLISH : GENDER_MAPPING,
)

function filterBySlug(
  array: FilterItemWithValues[],
  object: AppliedAttributeValues,
) {
  if (!array.length || !Object.keys(object).length) {
    return []
  }
  const slugs = Object.keys(object)
  return array.filter((item) => slugs.includes(item.slug))
}
function filterValues(
  filtered: FilterItemWithValues[],
  object: AppliedAttributeValues,
) {
  if (!filtered.length || !Object.keys(object).length) {
    return []
  }
  return filtered.map((item) => {
    const selectedIds = object[item.slug] || []
    return {
      ...item,
      values: item.values.filter((value) =>
        selectedIds.includes((value as FilterValue).id),
      ),
    }
  })
}
type RemappedResult = {
  [key: AttributesFilterItemWithValues['slug']]: AttributesFilterValue[]
}
function parseAppliedFilter(filteredResult: AttributesFilterItemWithValues[]) {
  if (!filteredResult.length) {
    return {} as RemappedResult
  }
  return filteredResult.reduce<RemappedResult>((acc, item) => {
    acc[item.slug] = item.values
    return acc
  }, {})
}

function getRootCategory(category: Category) {
  function getParent(category: Category) {
    if (category.parent) {
      return getParent(category.parent)
    }
    return category
  }

  return getParent(category)
}
function getSaleCategory(category: Category) {
  /** improve this to check if the category is a sale category locale */
  if (
    category.depth === 2 &&
    (category.slug === 'sale' ||
      (isPolishShop.value && category.slug === 'wyprzedaz'))
  ) {
    return category
  }
  if (category.parent) {
    return getSaleCategory(category.parent)
  }
  return null
}

function getPossessivePronoun(category: Category) {
  const normalizedCategoryName = category.name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^\w]/g, '')
  if (isPolishShop.value) {
    return capitalCase(
      genderMapping.value[
        normalizedCategoryName as keyof typeof genderMapping.value
      ],
    )
  }

  // if the category name ends with s, remove letter s
  const _categoryName = category.name.endsWith('s')
    ? category.name.slice(0, -1)
    : category.name
  return `${capitalCase(_categoryName)}'s`
}

function getFilteredResult() {
  const filtered = filterBySlug(
    availableFilters.value,
    appliedAttributeValues.value,
  )
  const withFilteredValues = filterValues(
    filtered,
    appliedAttributeValues.value,
  )
  return parseAppliedFilter(
    withFilteredValues as AttributesFilterItemWithValues[],
  )
}

function shouldApplySingleFilterValue(
  filterValues: AttributesFilterValue[] | undefined,
  hasOnlyOneFilterValue: boolean,
  hasOnlyBrandOrColorFilter: boolean,
) {
  return (
    filterValues &&
    filterValues.length === 1 &&
    hasOnlyOneFilterValue &&
    hasOnlyBrandOrColorFilter
  )
}

function applyBrandFilter(
  categoryName: string,
  result: RemappedResult,
  hasOnlyOneFilterValue: boolean,
  hasOnlyBrandOrColorFilter: boolean,
  isPolish: boolean,
) {
  if (
    !shouldApplySingleFilterValue(
      result.brand,
      hasOnlyOneFilterValue,
      hasOnlyBrandOrColorFilter,
    )
  ) {
    return categoryName
  }

  const brandName = result.brand.at(0)?.name ?? ''
  return isPolish
    ? `${categoryName} ${brandName}`
    : `${brandName} ${categoryName}`
}

function applyColorFilter(
  categoryName: string,
  result: RemappedResult,
  hasOnlyOneFilterValue: boolean,
  hasOnlyBrandOrColorFilter: boolean,
  isPolish: boolean,
) {
  if (
    !shouldApplySingleFilterValue(
      result.color,
      hasOnlyOneFilterValue,
      hasOnlyBrandOrColorFilter,
    )
  ) {
    return categoryName
  }

  const colorName = capitalCase(result.color.at(0)?.name ?? '')
  return isPolish
    ? `${categoryName} ${colorName} `
    : `${colorName} ${categoryName}`
}

function applyGenderPrefix(
  categoryName: string,
  rootCategory: Category | null,
  isPolish: boolean,
) {
  if (!rootCategory) {
    return categoryName
  }
  const possessive = getPossessivePronoun(rootCategory)
  return isPolish
    ? `${categoryName} ${possessive} `
    : `${possessive} ${categoryName}`
}

const computedCategoryName = computed(() => {
  const rootCategory = getRootCategory(category)
  const saleCategory = getSaleCategory(category)

  const hasAppliedFilters = Boolean(
    Object.keys(appliedAttributeValues.value).length,
  )

  const isRootGenderCategory = Object.keys(genderMapping.value).includes(
    rootCategory.name.toLowerCase() as keyof typeof genderMapping.value,
  )

  if (!hasAppliedFilters && !isRootGenderCategory) {
    return category.name
  }

  const hasOnlyOneFilterValue = Object.values(appliedAttributeValues.value)
    .filter(Boolean)
    .every((value) => value.length === 1)

  const hasOnlyBrandOrColorFilter = Object.keys(
    appliedAttributeValues.value,
  ).some((key) => key === 'brand' || key === 'color')

  const result = getFilteredResult()

  let categoryName = category.name

  if (saleCategory && saleCategory.name !== category.name) {
    categoryName = `${saleCategory?.name} ${categoryName}`
  }

  categoryName = applyGenderPrefix(
    categoryName,
    rootCategory,
    isPolishShop.value,
  )
  categoryName = applyBrandFilter(
    categoryName,
    result,
    hasOnlyOneFilterValue,
    hasOnlyBrandOrColorFilter,
    isPolishShop.value,
  )
  categoryName = applyColorFilter(
    categoryName,
    result,
    hasOnlyOneFilterValue,
    hasOnlyBrandOrColorFilter,
    isPolishShop.value,
  )

  return categoryName
})
</script>
